import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Camera, RotateCcw, Check, X, ArrowLeft, LogOut, AlertTriangle, FileText, Pencil } from "lucide-react";
import api from "@/lib/api";
import SignaturePadCanvas from "@/components/SignaturePadCanvas";

// Kiosk flow:  start  ->  form  ->  match  ->  capture  ->  done
//                                               |-> done (returning)
// Side flow:   start  ->  signout-form  ->  signout-list  ->  signout-done

function compressJpeg(canvas, targetMaxBytes = 60_000) {
  // Iterate quality from 0.78 down to 0.40 until under target.
  for (const q of [0.78, 0.7, 0.62, 0.55, 0.48, 0.4]) {
    const url = canvas.toDataURL("image/jpeg", q);
    const bytes = Math.floor((url.length - "data:image/jpeg;base64,".length) * 3 / 4);
    if (bytes <= targetMaxBytes) return { dataUrl: url, bytes };
  }
  const url = canvas.toDataURL("image/jpeg", 0.4);
  return { dataUrl: url, bytes: Math.floor((url.length) * 3 / 4) };
}

/**
 * Auto-AirPrint helper.
 *
 * After a visitor signs in, this component:
 *   1. Renders the backend-generated label PNG at full size (no UI chrome)
 *   2. Once the image has loaded, calls window.print()
 *   3. iOS Safari surfaces the AirPrint dialog — every iPad on the same Wi-Fi
 *      as the Brother QL-810W sees the printer, no Bluetooth pairing needed.
 *
 * Print CSS hides everything except the label so the printer only gets the
 * badge, not the welcome page chrome.
 */
function VisitorWelcome({ welcome }) {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  // Visitor just signs in — the reception PC handles badge printing from
  // the admin panel. No new tab, no preview, no AirPrint pop-up on the iPad.
  useEffect(() => {
    if (countdown <= 0) { navigate("/"); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, navigate]);

  return (
    <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-emerald-200 text-center space-y-5" data-testid="visitor-welcome">
      <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
        <Check className="w-12 h-12 text-emerald-600" />
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">Visitor Signed In</div>
        <h2 className="text-4xl font-bold text-slate-900 mt-2">{welcome.name}</h2>
      </div>
      <p className="text-slate-700 text-lg max-w-sm mx-auto">
        You're all set — please see the front desk for your badge.
      </p>
      <button
        onClick={() => navigate("/")}
        className="text-sm text-slate-500 hover:text-slate-700 underline"
        data-testid="visitor-done-btn"
      >
        Returning to sign-in screen in {countdown}s — tap to skip
      </button>
    </div>
  );
}



export default function VisitorSignIn() {
  const navigate = useNavigate();
  const [step, setStep] = useState("start");

  // sign-in form state
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [match, setMatch] = useState(null);            // /lookup response
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [cameraError, setCameraError] = useState("");
  const [welcome, setWelcome] = useState(null);         // { name, printer_configured }

  // sign-out state
  const [last4, setLast4] = useState("");
  const [signoutMatches, setSignoutMatches] = useState(null);
  const [signoutDone, setSignoutDone] = useState(null);

  // ----- Waiver state -----
  const [activeWaiver, setActiveWaiver] = useState(null);   // {id, version_number, text} | {active:false}
  const waiverScrollRef = useRef(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const sigRef = useRef(null);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [pendingSignIn, setPendingSignIn] = useState(null); // body that will be POSTed once waiver signed

  // ----- Sex-offender check / admin override state -----
  const [offenderCheck, setOffenderCheck] = useState(null); // { check_id, hit_count, hits }
  const [overridePass, setOverridePass] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideError, setOverrideError] = useState("");
  const [remoteStatus, setRemoteStatus] = useState(null);   // 'pending'|'approved'|'denied'

  // While the override modal is open, poll for an admin's remote decision so
  // the kiosk can auto-complete WITHOUT requiring a kiosk-side passcode entry.
  useEffect(() => {
    if (step !== "offender-override" || !offenderCheck?.check_id) return;
    let cancelled = false;
    setRemoteStatus("pending");
    const checkOnce = async () => {
      try {
        const r = await api.get(`/visitor/offender-check-status/${offenderCheck.check_id}`);
        if (cancelled) return;
        const status = r.data?.status;
        if (status === "admin_override") {
          setRemoteStatus("approved");
          // Auto re-submit /visitor/sign-in with check_id only (no token —
          // backend trusts the recently-recorded admin_override decision).
          const body = { ...(pendingSignIn || {}), offender_check_id: offenderCheck.check_id };
          try {
            const rr = await api.post("/visitor/sign-in", body);
            if (cancelled) return;
            stopCamera();
            setOffenderCheck(null);
            setPendingSignIn(null);
            setWelcome({ name: rr.data.name, printer_configured: rr.data.printer_configured, signin_id: rr.data.signin_id });
            setStep("done");
          } catch (e) {
            setOverrideError(e?.response?.data?.detail || "Could not complete sign-in after approval. Please try again.");
          }
        } else if (status === "admin_denied") {
          setRemoteStatus("denied");
          // Auto-bounce to lock screen after a brief notice.
          setTimeout(() => {
            if (cancelled) return;
            setOffenderCheck(null);
            setPendingSignIn(null);
            reset();
            navigate("/");
          }, 2500);
        }
      } catch { /* poll failure — retry next tick */ }
    };
    const id = setInterval(checkOnce, 2000);
    checkOnce();
    return () => { cancelled = true; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, offenderCheck?.check_id]);

  // Pull active waiver once on mount; re-fetch when reset() is called.
  const refreshActiveWaiver = useCallback(async () => {
    try {
      const r = await api.get("/visitor/active-waiver");
      setActiveWaiver(r.data);
    } catch { setActiveWaiver({ active: false }); }
  }, []);
  useEffect(() => { refreshActiveWaiver(); }, [refreshActiveWaiver]);

  // Camera plumbing
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: false,
      });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      setCameraError(e?.message || "Camera access was blocked. You can sign in without a photo (admin will review).");
    }
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // Welcome screen auto-dismiss (8s).
  useEffect(() => {
    if (step !== "done") return undefined;
    const t = setTimeout(() => navigate("/"), 8000);
    return () => clearTimeout(t);
  }, [step, navigate]);

  // Sign-out done auto-dismiss (4s).
  useEffect(() => {
    if (step !== "signout-done") return undefined;
    const t = setTimeout(() => navigate("/"), 4000);
    return () => clearTimeout(t);
  }, [step, navigate]);

  const reset = () => {
    setName(""); setPhone(""); setError(""); setMatch(null); setPhotoDataUrl(null);
    setCameraError(""); setWelcome(null); setLast4(""); setSignoutMatches(null); setSignoutDone(null);
    setOffenderCheck(null); setOverridePass(""); setOverrideReason(""); setOverrideError("");
    setPendingSignIn(null);
    stopCamera();
  };

  // -------------- step transitions --------------
  const onContinue = async (e) => {
    e?.preventDefault?.();
    setError("");
    const nm = name.trim();
    const digits = (phone || "").replace(/\D/g, "");
    if (!nm) return setError("Please enter your full name.");
    if (digits.length < 10) return setError("Please enter a valid phone number (10+ digits).");

    setSubmitting(true);
    try {
      const r = await api.post("/visitor/lookup", { name: nm, phone: digits });
      setMatch(r.data);
      if (r.data.match_type === "exact_phone") {
        // returning visitor: skip to confirmation, fire sign-in immediately
        await doSignIn({ returning_visitor_id: r.data.visitor_id });
      } else if (r.data.match_type === "name_only") {
        setStep("name-only-prompt");
      } else {
        // new visitor
        setStep("capture");
        startCamera();
      }
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhoneChangeYes = async () => {
    await doSignIn({
      confirm_phone_change: true,
      name_match_visitor_id: match?.visitor_id,
    });
  };
  const handlePhoneChangeNo = () => {
    setStep("capture");
    startCamera();
  };

  const doSignIn = async (extra = {}) => {
    setError("");
    const digits = (phone || "").replace(/\D/g, "");
    const body = {
      name: name.trim(),
      phone: digits,
      photo_base64: photoDataUrl ? photoDataUrl.split(",", 2)[1] : undefined,
      ...extra,
    };
    // If a waiver is active, defer the network call until AFTER the visitor
    // signs the waiver. (Spec: "if visitor cancels at signature step, no
    // records are created.")
    if (activeWaiver?.active) {
      setPendingSignIn(body);
      stopCamera();
      setScrolledToBottom(false);
      setSigEmpty(true);
      setStep("waiver");
      return;
    }
    await postSignIn(body);
  };

  const postSignIn = async (body) => {
    setSubmitting(true);
    try {
      const r = await api.post("/visitor/sign-in", body);
      // Sex-offender hit → don't enter `done`, surface the override modal.
      if (r.data?.requires_admin_override) {
        setOffenderCheck(r.data.offender_check || null);
        setPendingSignIn(body);
        stopCamera();
        setStep("offender-override");
        return;
      }
      stopCamera();
      setWelcome({ name: r.data.name, printer_configured: r.data.printer_configured, signin_id: r.data.signin_id });
      setStep("done");
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Sign-in failed. Please try again.");
      setStep(body?.photo_base64 ? "capture" : "form");
    } finally { setSubmitting(false); }
  };

  // ----- Admin override (sex-offender hit) -----
  const submitOverride = async ({ approve, passcode, reason }) => {
    setOverrideError("");
    if (approve && !(reason || "").trim()) {
      setOverrideError("Reason is required to approve a flagged check-in.");
      return;
    }
    setSubmitting(true);
    try {
      // Get a fresh admin token for this single override call.
      const auth = await api.post("/auth/admin-login", { passcode });
      const token = auth.data?.token;
      if (!token) throw new Error("no_token");
      if (!approve) {
        // DENY → record for audit and bounce visitor.
        try {
          await api.post(
            `/admin/offender-checks/${offenderCheck.check_id}/deny`,
            { reason: (reason || "").trim() || "denied at kiosk" },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } catch { /* audit best-effort */ }
        setOffenderCheck(null);
        setPendingSignIn(null);
        reset();
        navigate("/");
        return;
      }
      // APPROVE → re-submit the same payload with override fields.
      const body = {
        ...(pendingSignIn || {}),
        offender_check_id: offenderCheck.check_id,
        override_admin_token: token,
        override_reason: (reason || "").trim(),
      };
      const r = await api.post("/visitor/sign-in", body);
      stopCamera();
      setOffenderCheck(null);
      setPendingSignIn(null);
      setWelcome({ name: r.data.name, printer_configured: r.data.printer_configured, signin_id: r.data.signin_id });
      setStep("done");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : (e?.message === "no_token" ? "Wrong admin passcode."
        : "Override failed. Try again.");
      setOverrideError(msg);
    } finally { setSubmitting(false); }
  };

  // ----- Waiver scroll detection -----
  const onWaiverScroll = () => {
    const el = waiverScrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) setScrolledToBottom(true);
  };

  // If the waiver text is shorter than the visible area (tall iPad in
  // landscape, etc.), no scroll event fires — auto-unlock the button so the
  // visitor isn't stuck staring at a non-scrollable box.
  useEffect(() => {
    if (step !== "waiver" || !activeWaiver?.text) return;
    // give the layout a tick to compute heights
    const t = setTimeout(() => {
      const el = waiverScrollRef.current;
      if (!el) return;
      if (el.scrollHeight <= el.clientHeight + 4) setScrolledToBottom(true);
    }, 120);
    return () => clearTimeout(t);
  }, [step, activeWaiver?.text]);

  const goToSignature = () => {
    setStep("signature");
    setSigEmpty(true);
  };

  const submitSignedWaiver = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) return;
    const dataUrl = sigRef.current.toDataURL();
    const sigB64 = dataUrl ? dataUrl.split(",", 2)[1] : null;
    const body = {
      ...(pendingSignIn || {}),
      waiver_version_id: activeWaiver?.id,
      waiver_signature_image_base64: sigB64,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    };
    setStep("done-pending");
    // Stash the body so submitOverride() can re-POST it if the backend asks
    // for an admin override. postSignIn() also writes pendingSignIn when it
    // detects requires_admin_override; clearing it here would race that and
    // break the override resubmit (causing a 422 missing name/phone).
    setPendingSignIn(body);
    await postSignIn(body);
  };

  const cancelSignature = () => {
    // Spec: visitor cancels at signature step → entire sign-in cancelled, no records created.
    setPendingSignIn(null);
    reset();
    navigate("/");
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = 640;
    c.height = 480;
    const ctx = c.getContext("2d");
    // mirror so the kiosk preview matches the user; output is also mirrored
    ctx.save();
    ctx.translate(c.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, 0, 0, c.width, c.height);
    ctx.restore();
    const { dataUrl } = compressJpeg(c, 60_000);
    setPhotoDataUrl(dataUrl);
    stopCamera();
  };

  const retake = () => {
    setPhotoDataUrl(null);
    startCamera();
  };

  // -------------- sign-out flow --------------
  const submitSignout = async (e) => {
    e?.preventDefault?.();
    setError("");
    const d = (last4 || "").replace(/\D/g, "");
    if (d.length !== 4) return setError("Please enter the last 4 digits of your phone.");
    setSubmitting(true);
    try {
      const r = await api.post("/visitor/sign-out", { phone_last_4: d });
      if (r.data.error === "no_match") {
        setError("Phone not found. Please ask the front desk for help.");
      } else if (r.data.matches) {
        setSignoutMatches(r.data.matches);
        setStep("signout-list");
      } else if (r.data.ok) {
        setSignoutDone({ name: r.data.name });
        setStep("signout-done");
      }
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const pickSignoutMatch = async (signinId) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/visitor/sign-out/${signinId}`);
      setSignoutDone({ name: r.data.name });
      setStep("signout-done");
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Sign-out failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // ===================== UI =====================
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50" data-testid="visitor-page">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur border-b border-amber-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => { reset(); navigate("/"); }} className="p-2 rounded-lg hover:bg-amber-100" data-testid="visitor-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-700" />
        </button>
        <UserPlus className="w-6 h-6 text-amber-600" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Rolling River Day Camp</div>
          <h1 className="text-lg font-display font-bold text-slate-900">Visitor Sign-In</h1>
        </div>
      </div>

      <div className="flex-1 flex items-start justify-center p-4 sm:p-8">
        <div className="w-full max-w-xl">

          {/* ---------- STEP: start ---------- */}
          {step === "start" && (
            <div className="space-y-4" data-testid="visitor-start">
              <button
                onClick={() => { reset(); setStep("form"); }}
                className="w-full rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg sm:text-xl px-6 py-6 shadow-lg shadow-amber-500/30 inline-flex items-center justify-center gap-3"
                data-testid="visitor-signin-cta"
              >
                <UserPlus className="w-6 h-6" /> Sign In as a Visitor
              </button>
              <button
                onClick={() => { reset(); setStep("signout-form"); }}
                className="w-full rounded-2xl bg-white hover:bg-amber-50 text-amber-700 border-2 border-amber-300 font-bold text-base px-6 py-4 inline-flex items-center justify-center gap-2"
                data-testid="visitor-signout-cta"
              >
                <LogOut className="w-5 h-5" /> Sign Out (Returning Visitor)
              </button>
              <div className="text-center text-xs text-slate-500 pt-2">
                Vendors, parents, contractors, and inspectors only. Staff: please use the blue Staff Sign-In button on the lock screen.
              </div>
            </div>
          )}

          {/* ---------- STEP: form ---------- */}
          {step === "form" && (
            <form onSubmit={onContinue} className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-amber-100 space-y-5" data-testid="visitor-form">
              <h2 className="text-xl font-bold text-slate-900">Welcome — let's get you signed in.</h2>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold mb-1 block">Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={60}
                  autoFocus
                  placeholder="Jane Smith"
                  className="w-full text-lg px-4 py-3 rounded-xl bg-amber-50/40 border border-amber-200 outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="visitor-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold mb-1 block">Phone Number</label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full text-lg px-4 py-3 rounded-xl bg-amber-50/40 border border-amber-200 outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="visitor-phone-input"
                />
              </div>

              {error && (
                <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="visitor-form-error">{error}</div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg py-4 disabled:opacity-50"
                data-testid="visitor-continue-btn"
              >
                {submitting ? "Checking…" : "Continue →"}
              </button>
              <button type="button" onClick={() => navigate("/")} className="block mx-auto text-sm text-slate-500 hover:text-slate-800" data-testid="visitor-cancel-btn">
                Cancel
              </button>
            </form>
          )}

          {/* ---------- STEP: name-only-prompt ---------- */}
          {step === "name-only-prompt" && match && (
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-amber-100 space-y-5" data-testid="visitor-name-prompt">
              <div className="flex items-start gap-4">
                {match.photo_base64 && (
                  <img src={`data:image/jpeg;base64,${match.photo_base64}`} alt="prior visit" className="w-24 h-24 rounded-2xl object-cover border border-amber-200" />
                )}
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Hi {match.name}!</h2>
                  <p className="text-sm text-slate-600 mt-1">
                    We have you on file from a previous visit
                    {match.prior_phone_display ? <> — phone <strong>{match.prior_phone_display}</strong></> : null}.
                    Did your phone number change?
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={handlePhoneChangeYes} disabled={submitting}
                        className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 disabled:opacity-50"
                        data-testid="visitor-phone-yes">
                  Yes, that's me
                </button>
                <button onClick={handlePhoneChangeNo} disabled={submitting}
                        className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-4"
                        data-testid="visitor-phone-no">
                  No, different person
                </button>
              </div>
              {error && <div className="text-red-700 text-sm">{error}</div>}
            </div>
          )}

          {/* ---------- STEP: capture ---------- */}
          {step === "capture" && (
            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-amber-100 space-y-5" data-testid="visitor-capture">
              <h2 className="text-xl font-bold text-slate-900">Take Your Photo</h2>
              {!photoDataUrl ? (
                <>
                  {cameraError ? (
                    <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-red-800" data-testid="visitor-camera-error">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                        <div className="text-sm">{cameraError}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video bg-black rounded-2xl overflow-hidden">
                      <video ref={videoRef} className="w-full h-full object-cover -scale-x-100" playsInline muted />
                    </div>
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                  <div className="flex gap-3">
                    {!cameraError && (
                      <button onClick={takePhoto} className="flex-1 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold py-4 inline-flex items-center justify-center gap-2"
                              data-testid="visitor-take-photo-btn">
                        <Camera className="w-5 h-5" /> Take Photo
                      </button>
                    )}
                    <button onClick={() => doSignIn()} disabled={submitting}
                            className="flex-1 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-4"
                            data-testid="visitor-skip-photo-btn">
                      Sign In Without Photo
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <img src={photoDataUrl} alt="visitor preview" className="w-full max-h-80 object-contain rounded-2xl bg-slate-100" data-testid="visitor-photo-preview" />
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={retake} className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-4 inline-flex items-center justify-center gap-2"
                            data-testid="visitor-retake-btn">
                      <RotateCcw className="w-5 h-5" /> Retake
                    </button>
                    <button onClick={() => doSignIn()} disabled={submitting}
                            className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="visitor-use-photo-btn">
                      <Check className="w-5 h-5" /> Use This Photo
                    </button>
                  </div>
                </>
              )}
              {error && <div className="text-red-700 text-sm" data-testid="visitor-capture-error">{error}</div>}
            </div>
          )}

          {/* ---------- STEP: waiver (read & scroll) ---------- */}
          {step === "waiver" && activeWaiver?.active && (
            <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-xl border border-amber-200 space-y-4" data-testid="visitor-waiver">
              <div className="flex items-start gap-3">
                <FileText className="w-6 h-6 text-amber-700 mt-1 shrink-0" />
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Visitor Waiver</h2>
                  <p className="text-sm text-slate-600">Please read carefully and scroll to the bottom.</p>
                </div>
              </div>
              <div
                ref={waiverScrollRef}
                onScroll={onWaiverScroll}
                className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-800 text-[15px] leading-relaxed overflow-y-auto overscroll-contain"
                style={{
                  maxHeight: "55vh",
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  WebkitOverflowScrolling: "touch",  // iOS Safari momentum scroll
                  touchAction: "pan-y",              // explicit vertical pan (parents must not block)
                }}
                data-testid="visitor-waiver-text"
              >
                {activeWaiver.text}
              </div>
              <div className="text-xs text-slate-500 italic flex items-center justify-between gap-2 flex-wrap">
                <span className="font-mono">v{activeWaiver.version_number}</span>
                {!scrolledToBottom && (
                  <button
                    type="button"
                    onClick={() => {
                      const el = waiverScrollRef.current;
                      if (el) {
                        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
                        // Also unlock immediately — iOS scrollend events are unreliable.
                        setTimeout(() => setScrolledToBottom(true), 350);
                      } else {
                        setScrolledToBottom(true);
                      }
                    }}
                    className="text-amber-700 underline font-semibold inline-flex items-center gap-1"
                    data-testid="visitor-waiver-jump-bottom"
                  >
                    <AlertTriangle className="w-3 h-3" /> Tap to jump to bottom
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={cancelSignature}
                        className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-4"
                        data-testid="visitor-waiver-cancel">
                  Cancel
                </button>
                <button onClick={goToSignature} disabled={!scrolledToBottom}
                        className={`rounded-xl font-bold py-4 inline-flex items-center justify-center gap-2 ${
                          scrolledToBottom ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-slate-200 text-slate-400 cursor-not-allowed"
                        }`}
                        data-testid="visitor-waiver-agree">
                  <Pencil className="w-5 h-5" /> I have read and agree, sign below
                </button>
              </div>
            </div>
          )}

          {/* ---------- STEP: signature ---------- */}
          {step === "signature" && (
            <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-xl border border-amber-200 space-y-4" data-testid="visitor-signature-step">
              <h2 className="text-xl font-bold text-slate-900">Sign Below</h2>
              <p className="text-sm text-slate-600">Use your finger to sign in the box. Tap <strong>Clear</strong> to redo.</p>
              <SignaturePadCanvas ref={sigRef} onEmptyChange={setSigEmpty} height={240} />
              <div className="grid grid-cols-3 gap-2">
                <button onClick={cancelSignature}
                        className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-3"
                        data-testid="visitor-signature-cancel">
                  Cancel
                </button>
                <button onClick={() => sigRef.current?.clear()}
                        className="rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold py-3"
                        data-testid="visitor-signature-clear-btn">
                  Clear
                </button>
                <button onClick={submitSignedWaiver}
                        disabled={sigEmpty || submitting}
                        className="rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 disabled:opacity-50"
                        data-testid="visitor-signature-confirm">
                  Confirm
                </button>
              </div>
            </div>
          )}

          {/* ---------- STEP: done-pending (post-waiver, before backend reply) ---------- */}
          {step === "done-pending" && (
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-emerald-200 text-center space-y-3" data-testid="visitor-done-pending">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Signing you in…</h2>
            </div>
          )}

          {/* ---------- STEP: offender-override ---------- */}
          {step === "offender-override" && offenderCheck && (
            <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-red-300 space-y-5" data-testid="visitor-offender-override">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-red-700 font-bold">Restricted — Admin Required</div>
                  <h2 className="text-lg font-bold text-slate-900">
                    Possible registry match — {offenderCheck.hit_count} record{offenderCheck.hit_count === 1 ? "" : "s"} returned
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    The 51-state sex-offender registry returned matches for this name. Sign-in is held until an admin reviews and approves or denies.
                  </p>
                  {remoteStatus === "approved" && (
                    <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold" data-testid="remote-status-approved">
                      ✓ Approved by admin — completing sign-in…
                    </div>
                  )}
                  {remoteStatus === "denied" && (
                    <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-md bg-slate-100 border border-slate-300 text-slate-800 text-xs font-bold" data-testid="remote-status-denied">
                      ✗ Denied by admin — returning to lock screen…
                    </div>
                  )}
                  {remoteStatus === "pending" && (
                    <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold animate-pulse" data-testid="remote-status-pending">
                      Waiting for admin decision… (an alert email was sent)
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 max-h-64 overflow-auto p-3 space-y-2 text-xs" data-testid="offender-hits-list">
                {(offenderCheck.hits || []).slice(0, 10).map((h, i) => (
                  <div key={i} className="rounded-lg bg-white border border-slate-200 p-2 flex gap-2">
                    {h.offenderImageUrl ? (
                      <img src={h.offenderImageUrl} alt="" className="w-12 h-12 rounded object-cover bg-slate-100" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-slate-100" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 truncate">{h.name || `${h.firstName || ""} ${h.lastName || ""}`}</div>
                      <div className="text-slate-600 truncate">{[h.city, h.state, h.zipcode].filter(Boolean).join(", ")}</div>
                      {h.dob && <div className="text-slate-500">DOB: {String(h.dob).slice(0, 10)}</div>}
                      {h.crime && <div className="text-slate-500 line-clamp-2">{h.crime}</div>}
                      {h.offenderUrl && (
                        <a href={h.offenderUrl} target="_blank" rel="noreferrer" className="text-amber-700 underline">
                          State registry record →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {(offenderCheck.hits || []).length > 10 && (
                  <div className="text-center text-slate-500">+ {offenderCheck.hits.length - 10} more — review in admin panel</div>
                )}
              </div>

              <div className="space-y-2 pt-2 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Admin override</div>
                <input
                  type="password" autoFocus value={overridePass}
                  onChange={(e) => setOverridePass(e.target.value)}
                  placeholder="Admin passcode"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="offender-override-passcode"
                />
                <textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  rows={2} maxLength={500}
                  placeholder="Reason / decision notes (required to approve)"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-amber-400 text-sm"
                  data-testid="offender-override-reason"
                />
                {overrideError && <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="offender-override-error">{overrideError}</div>}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => submitOverride({ approve: false, passcode: overridePass, reason: overrideReason })}
                    disabled={submitting || !overridePass}
                    className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-3 disabled:opacity-50"
                    data-testid="offender-override-deny">
                    Deny &amp; cancel
                  </button>
                  <button
                    onClick={() => submitOverride({ approve: true, passcode: overridePass, reason: overrideReason })}
                    disabled={submitting || !overridePass || !overrideReason.trim()}
                    className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 disabled:opacity-50"
                    data-testid="offender-override-approve">
                    {submitting ? "Working…" : "Approve & sign in"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---------- STEP: done (welcome) ---------- */}
          {step === "done" && welcome && (
            <VisitorWelcome welcome={welcome} />
          )}

          {/* ---------- STEP: signout-form ---------- */}
          {step === "signout-form" && (
            <form onSubmit={submitSignout} className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-amber-100 space-y-5" data-testid="visitor-signout-form">
              <h2 className="text-xl font-bold text-slate-900">Visitor Sign-Out</h2>
              <p className="text-sm text-slate-600">Enter the last 4 digits of the phone number you signed in with.</p>
              <input
                type="tel"
                inputMode="numeric"
                value={last4}
                onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="1234"
                autoFocus
                maxLength={4}
                className="w-full text-3xl tracking-[0.6em] text-center px-4 py-4 rounded-xl bg-amber-50/40 border border-amber-200 outline-none focus:ring-2 focus:ring-amber-400 font-mono"
                data-testid="visitor-last4-input"
              />
              {error && <div className="text-red-700 text-sm">{error}</div>}
              <button type="submit" disabled={submitting || last4.length !== 4}
                      className="w-full rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-lg py-4 disabled:opacity-50"
                      data-testid="visitor-signout-submit">
                {submitting ? "Searching…" : "Sign Out →"}
              </button>
              <button type="button" onClick={() => navigate("/")} className="block mx-auto text-sm text-slate-500 hover:text-slate-800">Cancel</button>
            </form>
          )}

          {/* ---------- STEP: signout-list (multi-match) ---------- */}
          {step === "signout-list" && signoutMatches && (
            <div className="bg-white rounded-3xl p-6 shadow-xl border border-amber-100 space-y-3" data-testid="visitor-signout-list">
              <h2 className="text-lg font-bold text-slate-900">Multiple matches — please pick yours</h2>
              {signoutMatches.map((m) => (
                <button key={m.signin_id} onClick={() => pickSignoutMatch(m.signin_id)} disabled={submitting}
                        className="w-full text-left rounded-xl border border-amber-200 hover:bg-amber-50 px-4 py-3 flex items-center justify-between"
                        data-testid={`visitor-signout-match-${m.signin_id}`}>
                  <div>
                    <div className="font-semibold text-slate-900">{m.name}</div>
                    <div className="text-xs text-slate-500">Signed in {new Date(m.signed_in_at).toLocaleString()}</div>
                  </div>
                  <LogOut className="w-5 h-5 text-amber-600" />
                </button>
              ))}
              <button onClick={() => { reset(); setStep("signout-form"); }} className="block mx-auto text-sm text-slate-500 hover:text-slate-800 mt-2">
                <X className="w-4 h-4 inline mr-1" /> None of these
              </button>
            </div>
          )}

          {/* ---------- STEP: signout-done ---------- */}
          {step === "signout-done" && signoutDone && (
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-emerald-200 text-center space-y-3" data-testid="visitor-signout-done">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">Goodbye, {signoutDone.name}!</h2>
              <p className="text-xs text-slate-400">Returning to lock screen…</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
